import { Badge } from "../../ui/badge";
import { Fact, Facts } from "../../ui/facts";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { Panel } from "../../ui/panel";
import { hintClass, listClass, SubHeading } from "../components/parts";
import type { CustomerQuestionView, CustomerSeesModel } from "../detail-model";
import { formatBytes } from "../format";
import { fieldTypeLabels } from "../labels";

// "What the customer sees". This block renders a `CustomerSeesModel` and NOTHING else: no `detail`, no `facts`, no note. The model is
// built by `buildCustomerSees` from a closed list of keys (status text, the staff-set status line, and the prompt, fields and the
// customer's own answer of each question). A staff-only field has no way in; the sentinel test proves it.

function QuestionBlock({ question }: Readonly<{ question: CustomerQuestionView }>) {
  return (
    <li className="grid gap-2">
      <p className="m-0">
        <strong>Question {question.sequence}:</strong> {question.prompt}
      </p>
      {question.fields.length === 0 ? null : (
        <ul
          className="m-0 grid list-none gap-1 p-0 text-sm"
          aria-label={`Fields of question ${question.sequence}`}
        >
          {question.fields.map((field) => (
            <li key={field.key}>
              {field.label} (
              {fieldTypeLabels[field.type as keyof typeof fieldTypeLabels] ?? field.type}
              {field.required ? ", required" : ", optional"})
            </li>
          ))}
        </ul>
      )}
      {question.answer === null ? (
        <p className={hintClass}>
          {question.status === "WITHDRAWN"
            ? "Withdrawn. The customer did not answer."
            : "Not answered yet."}
        </p>
      ) : (
        <Facts>
          {question.answer.map(([label, value]) => (
            <Fact key={label} term={label}>
              {value}
            </Fact>
          ))}
        </Facts>
      )}
    </li>
  );
}

export function CustomerSeesSection({ model }: Readonly<{ model: CustomerSeesModel }>) {
  return (
    <Panel
      id="request-customer-sees-title"
      title="What the customer sees (as the app shows it)"
      description="Exactly the words and questions the customer can read. Internal notes, reasons, evidence and the work status are not part of this block."
      testId="customer-sees"
      className="border-2 border-primary"
    >
      <Facts>
        <Fact term="Status">{model.statusText}</Fact>
        {model.statusLine === null ? null : (
          <Fact term="Line from your team">{model.statusLine}</Fact>
        )}
      </Facts>
      {model.openQuestion === null ? null : (
        <Notice tone="warning">
          <p>
            <strong className="inline-flex items-center gap-1.5">
              <Icon name="pause" className="size-4" />
              Needs the customer now:
            </strong>
          </p>
          <ul className={`${listClass} w-full`}>
            <QuestionBlock question={model.openQuestion} />
          </ul>
        </Notice>
      )}
      {model.updates.length === 0 ? null : (
        <>
          <SubHeading>Updates from your team</SubHeading>
          <ul className={listClass} aria-label="Updates the customer sees">
            {model.updates.map((update) => (
              <li
                key={`${update.createdAt}-${update.text}`}
                className="flex flex-wrap items-baseline gap-2"
              >
                {update.isCorrection ? (
                  <Badge tone="warning">
                    <Icon name="file-edit" />
                    Corrected
                  </Badge>
                ) : null}
                <span className="break-words">{update.text}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {model.attachments.length === 0 ? null : (
        <>
          <SubHeading>Files shared with the customer</SubHeading>
          <ul className={`${listClass} gap-1`} aria-label="Files the customer sees">
            {model.attachments.map((attachment) => (
              <li key={`${attachment.displayName}-${attachment.sharedAt}`}>
                {attachment.displayName} ({attachment.contentType},{" "}
                {formatBytes(attachment.sizeBytes)})
              </li>
            ))}
          </ul>
        </>
      )}
      {model.questionHistory.length === 0 ? null : (
        <>
          <SubHeading>Earlier questions</SubHeading>
          <ul className={listClass} aria-label="Earlier questions the customer saw">
            {model.questionHistory.map((question) => (
              <QuestionBlock key={question.sequence} question={question} />
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}
